import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import img from "../assets/1.jpg";

const FadeIn = ({ children, delay = 0, className = "" }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
};

const Stat = ({ value, label, suffix = "" }) => (
    <FadeIn>
        <div className="text-center p-6 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-amber-500/30 transition-colors duration-300">
            <div className="text-4xl md:text-5xl font-black text-white mb-2">
                {value}<span className="text-amber-500">{suffix}</span>
            </div>
            <div className="text-sm text-slate-400 uppercase tracking-wider">{label}</div>
        </div>
    </FadeIn>
);

const ValueCard = ({ icon, title, description, delay }) => (
    <FadeIn delay={delay}>
        <div className="group p-6 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-amber-500/30 hover:-translate-y-1 transition-all duration-300">
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 text-xl mb-5 group-hover:scale-110 transition-transform">
                <i className={`bi ${icon}`} aria-hidden="true" />
            </div>
            <h3 className="text-lg font-bold text-white mb-3">{title}</h3>
            <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
        </div>
    </FadeIn>
);

export default function AboutUs() {
    return (
        <div className="bg-slate-950 text-white py-24">
            {/* Hero Section */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent pointer-events-none" />

                <div className="max-w-7xl mx-auto relative z-10">
                    <FadeIn>
                        <span className="inline-flex items-center justify-center w-full gap-2 text-amber-500 uppercase tracking-widest text-sm font-semibold font-heading mb-4">
                            <span className="w-2 h-2 rounded-full bg-amber-500" />
                            Who We Are
                            <span className="w-2 h-2 rounded-full bg-amber-500" />
                        </span>
                    </FadeIn>

                    <FadeIn delay={100} className="text-center">
                        <h1 className="text-5xl md:text-7xl font-black tracking-tight font-heading mb-6">
                            About <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Us</span>
                        </h1>
                    </FadeIn>

                    <FadeIn delay={200}>
                        <p className="text-xl text-slate-400 max-w-3xl leading-relaxed mx-auto text-center">
                            Milestone and Delight Galaxy Limited is a Kampala-based technology company
                            delivering enterprise software, ERP solutions, and digital infrastructure
                            to businesses across East Africa.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Story + Image */}
            <section className="py-5 px-6">
                <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
                    <FadeIn>
                        <div className="relative group">
                            <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/20 to-blue-500/20 rounded-3xl blur-2xl opacity-50 group-hover:opacity-75 transition-opacity duration-500" />
                            <img
                                src={img}
                                alt="Delight Galaxy team collaborating on software solutions"
                                className="relative w-full h-[400px] lg:h-[500px] object-cover rounded-2xl border border-slate-800 shadow-2xl"
                            />
                            <div className="absolute bottom-6 left-6 right-6 p-6 bg-slate-950/90 backdrop-blur-xl rounded-xl border border-slate-800">
                                <p className="text-sm text-slate-300 italic">
                                    "Technology should solve real problems, not create new ones. That's the
                                    principle behind every solution we build."
                                </p>
                                <p className="text-xs text-amber-500 font-bold mt-2 uppercase tracking-wider">
                                    — Founding Team
                                </p>
                            </div>
                        </div>
                    </FadeIn>

                    <div className="space-y-8">
                        <FadeIn delay={150}>
                            <div>
                                <h2 className="text-sm font-semibold tracking-widest uppercase text-amber-500 mb-3">
                                    Our Story
                                </h2>
                                <h3 className="text-3xl md:text-4xl font-bold font-heading leading-tight mb-4">
                                    Built by Engineers, <br />For Business Leaders
                                </h3>
                                <p className="text-slate-400 leading-relaxed">
                                    Founded in Kampala, we started with a simple mission: make enterprise-grade
                                    technology accessible to African businesses. From SAP Business One implementations
                                    to custom EFRIS integrations, we've grown into a full-service digital
                                    transformation partner.
                                </p>
                            </div>
                        </FadeIn>

                        <FadeIn delay={250}>
                            <div className="grid sm:grid-cols-2 gap-6">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-500 shrink-0">
                                        <i className="bi bi-bullseye" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white mb-1">Our Mission</h4>
                                        <p className="text-sm text-slate-400">
                                            Empower organizations with reliable, scalable software that drives measurable growth.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 shrink-0">
                                        <i className="bi bi-eye" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white mb-1">Our Vision</h4>
                                        <p className="text-sm text-slate-400">
                                            Become East Africa's most trusted technology partner for enterprise digital transformation.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </FadeIn>

                        <FadeIn delay={350}>
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-amber-500 text-slate-950 font-bold hover:bg-amber-400 hover:shadow-lg hover:shadow-amber-500/20 hover:-translate-y-0.5 transition-all duration-300"
                            >
                                Work With Us
                                <i className="bi bi-arrow-right" aria-hidden="true" />
                            </Link>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* Core Values Section */}
            <section className="py-24 px-6 border-t border-slate-900 bg-slate-950/30">
                <div className="max-w-7xl mx-auto">
                    <FadeIn className="text-center mb-16">
                        <span className="inline-flex items-center justify-center gap-2 text-amber-500 uppercase tracking-widest text-xs font-semibold font-heading mb-4">
                            <span className="w-2 h-2 rounded-full bg-amber-500" />
                            Our Foundations
                            <span className="w-2 h-2 rounded-full bg-amber-500" />
                        </span>
                        <h2 className="text-4xl md:text-5xl font-black font-heading text-white">
                            Our Core <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Values</span>
                        </h2>
                    </FadeIn>

                    <div className="grid md:grid-cols-3 gap-8">
                        <ValueCard
                            icon="bi-shield-check"
                            title="Integrity First"
                            description="We maintain absolute transparency and security in all data compliance and electronic invoicing integrations."
                            delay={100}
                        />
                        <ValueCard
                            icon="bi-lightbulb"
                            title="Continuous Innovation"
                            description="We leverage the latest technological advancements to build future-proof custom cloud systems and mobile platforms."
                            delay={200}
                        />
                        <ValueCard
                            icon="bi-hand-thumbs-up"
                            title="Client Empowerment"
                            description="We focus on solutions that grow with our clients, enabling businesses to automate and scale without friction."
                            delay={300}
                        />
                    </div>
                </div>
            </section>
        </div>
    );
}